import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  ConfirmModal,
  Edit,
  ErrorState,
  Input,
  Modal,
  Trash,
} from '@react-apps/design-system';
import { EntityLink } from '@/shared/components/entity-link';
import { IconButton } from '@/shared/components/icon-button';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import { OfferSearchInput } from '@/shared/offer-search/OfferSearchInput';
import { readApiErrorCode } from '@/shared/offer-search/offer-errors';
import { errorToastId, readApiErrorMessage } from '@/shared/request-feedback';
import {
  validateGuaranteeRule,
  type GuaranteeCampaignDetail,
  type GuaranteeCampaignOffer,
  type GuaranteeRuleErrors,
} from '../../domain/entities/guarantee.entity';
import {
  campaignRenameKey,
  useAddGuaranteeCampaignOffer,
  useGuaranteeCampaign,
  useRemoveGuaranteeCampaignOffer,
  useRenameGuaranteeCampaign,
  useUpdateGuaranteeCampaignRule,
} from '../hooks/use-guarantee-campaigns';
import { GuaranteeCampaignFormModal } from '../components/GuaranteeCampaignFormModal';
import {
  draftToRule,
  GuaranteeRuleFields,
  RULE_FIELDS,
  ruleToDraft,
} from '../components/GuaranteeRuleFields';
import { formatDate } from '../format';

const LIST_PATH = '/guarantee/campaigns';

export function GuaranteeCampaignDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, isError, error, refetch } = useGuaranteeCampaign(id);
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <BackLink />
      {isLoading ? (
        <div
          aria-busy="true"
          className="border-(--color-border) bg-(--color-bg-primary) h-96 animate-pulse rounded-xl border"
        />
      ) : isError && status === 404 ? (
        <ErrorState message="Esta campanha não existe. Volte para a lista e escolha outra." />
      ) : isError || !data ? (
        <ErrorState
          message="Não foi possível carregar a campanha."
          onRetry={() => void refetch()}
        />
      ) : (
        <CampaignDetail campaign={data} />
      )}
    </div>
  );
}

function BackLink() {
  const goBack = useBackNavigate(LIST_PATH);
  return (
    <button
      type="button"
      onClick={goBack}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm transition-colors"
    >
      ← Campanhas de garantia
    </button>
  );
}

function CampaignDetail({ campaign }: { campaign: GuaranteeCampaignDetail }) {
  const [renaming, setRenaming] = useState(false);
  return (
    <>
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            {campaign.name}
          </h1>
          <IconButton
            label="Renomear campanha"
            icon={<Edit size="sm" />}
            onClick={() => setRenaming(true)}
          />
        </div>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          Criada em {formatDate(campaign.createdAt)} ·{' '}
          {campaign.activeGuarantees === 1
            ? '1 pessoa com garantia ativa'
            : `${campaign.activeGuarantees} pessoas com garantia ativa`}
        </p>
      </div>

      <OffersSection campaign={campaign} />
      {/* `key`: só régua ou trava mudando reiniciam o rascunho. Renomear ou
          mexer em oferta também muda o `updatedAt`, e não pode apagar o que
          está sendo digitado. */}
      <RuleSection
        key={`${campaign.ruleLocked}:${JSON.stringify(campaign.rule)}`}
        campaign={campaign}
      />

      <RenameModal
        open={renaming}
        campaign={campaign}
        onClose={() => setRenaming(false)}
      />
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-6">
      <h2 className="text-(--color-text-primary) text-sm font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

function OffersSection({ campaign }: { campaign: GuaranteeCampaignDetail }) {
  const addOffer = useAddGuaranteeCampaignOffer(campaign.id);
  const removeOffer = useRemoveGuaranteeCampaignOffer(campaign.id);
  const [addError, setAddError] = useState<string>();
  const [removing, setRemoving] = useState<GuaranteeCampaignOffer | null>(null);

  const closeRemove = () => {
    setRemoving(null);
    removeOffer.reset();
  };

  return (
    <Section title="Ofertas">
      <OfferSearchInput
        id="guarantee-campaign-add-offer"
        label="Adicionar oferta"
        error={addError}
        readOnly={addOffer.isPending}
        onTermChange={() => setAddError(undefined)}
        onSelect={(offer) => {
          setAddError(undefined);
          addOffer.mutate(
            {
              providerAccountId: offer.providerAccountId,
              offerExternalId: offer.externalId,
            },
            {
              // O toast diz QUE falhou mesmo com o campo fora da tela; o mesmo
              // id troca a mensagem crua da rede global pela daqui
              // (`request-feedback.ts`).
              onError: (err) => {
                const message = addOfferErrorMessage(err);
                setAddError(message);
                toast.error(message, { id: errorToastId(err) });
              },
            },
          );
        }}
      />

      {campaign.offers.length === 0 ? (
        <p className="text-(--color-text-secondary) text-sm">
          Adicione a primeira oferta. Sem ela, nenhuma venda abre garantia.
        </p>
      ) : (
        <ul className="divide-(--color-border) divide-y">
          {campaign.offers.map((offer) => {
            const label = offer.name ?? offer.offerExternalId;
            return (
              <li
                key={`${offer.providerAccountId}:${offer.offerExternalId}`}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <p className="text-(--color-text-primary) truncate text-sm">
                    <EntityLink
                      kind="offer"
                      id={offer.offerId}
                      fallback={label}
                    >
                      {label}
                    </EntityLink>
                  </p>
                  <p className="text-(--color-text-secondary) truncate text-xs">
                    {offer.productName ? `${offer.productName} · ` : ''}
                    {offer.providerAccountName ?? offer.providerAccountId}
                  </p>
                </div>
                <IconButton
                  label={`Remover a oferta ${label}`}
                  icon={<Trash size="sm" />}
                  variant="danger"
                  onClick={() => setRemoving(offer)}
                />
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmModal
        open={!!removing}
        onClose={closeRemove}
        onConfirm={() => {
          if (!removing) return;
          removeOffer.mutate(
            {
              providerAccountId: removing.providerAccountId,
              offerExternalId: removing.offerExternalId,
            },
            { onSuccess: closeRemove },
          );
        }}
        title="Remover oferta"
        description={`As próximas vendas de ${removing?.name ?? removing?.offerExternalId ?? ''} deixam de abrir garantia. As garantias já abertas por ela continuam valendo.`}
        confirmLabel="Remover"
        loading={removeOffer.isPending}
        errorMessage={
          removeOffer.error
            ? readApiErrorMessage(removeOffer.error, 'Falha ao remover.')
            : null
        }
      />
    </Section>
  );
}

function addOfferErrorMessage(err: unknown): string {
  switch (readApiErrorCode(err)) {
    case 'GUARANTEE_CAMPAIGN_OFFER_ALREADY_EXISTS':
      return 'Esta oferta já está numa campanha — nesta ou em outra. Cada oferta abre garantia por uma campanha só.';
    case 'GUARANTEE_OFFER_NOT_REGISTERED':
      return 'Esta oferta não está no Sales. Cadastre-a pela busca antes.';
  }
  return readApiErrorMessage(err, 'Não foi possível adicionar a oferta.');
}

function RuleSection({ campaign }: { campaign: GuaranteeCampaignDetail }) {
  const updateRule = useUpdateGuaranteeCampaignRule(campaign.id);
  const [draft, setDraft] = useState(() => ruleToDraft(campaign.rule));
  const [errors, setErrors] = useState<GuaranteeRuleErrors>({});
  const [duplicating, setDuplicating] = useState(false);

  if (campaign.ruleLocked) {
    return (
      <Section title="Régua">
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          A régua está travada: já há garantias abertas com ela, e mudá-la agora
          mudaria a promessa feita a quem já comprou. Para usar outra régua,
          duplique como campanha nova.
        </p>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          {RULE_FIELDS.map(({ field, label }) => (
            <div key={field}>
              <dt className="text-(--color-text-secondary) text-xs">{label}</dt>
              <dd className="text-(--color-text-primary)">
                {ruleToDraft(campaign.rule)[field]}
              </dd>
            </div>
          ))}
        </dl>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => setDuplicating(true)}>
            Duplicar como campanha nova
          </Button>
        </div>
        <GuaranteeCampaignFormModal
          open={duplicating}
          onClose={() => setDuplicating(false)}
          initialRule={campaign.rule}
        />
      </Section>
    );
  }

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const rule = draftToRule(draft);
    const next = validateGuaranteeRule(rule);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    updateRule.mutate(rule, {
      onSuccess: () => toast.success('Régua salva.'),
      onError: (err) => {
        if (readApiErrorCode(err) === 'INVALID_GUARANTEE_CAMPAIGN_STATE') {
          toast.error(
            'A régua travou enquanto você editava: uma garantia acabou de abrir com ela.',
            { id: errorToastId(err) },
          );
        }
      },
    });
  };

  return (
    <Section title="Régua">
      <p className="text-(--color-text-secondary) text-sm">
        Editável enquanto nenhuma garantia usar esta campanha. Depois da
        primeira, ela trava.
      </p>
      <form noValidate onSubmit={save} className="space-y-4">
        <GuaranteeRuleFields
          idPrefix="guarantee-campaign-rule"
          value={draft}
          onChange={setDraft}
          errors={errors}
          readOnly={updateRule.isPending}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={updateRule.isPending}>
            {updateRule.isPending ? 'Salvando…' : 'Salvar régua'}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function RenameModal({
  open,
  campaign,
  onClose,
}: {
  open: boolean;
  campaign: GuaranteeCampaignDetail;
  onClose: () => void;
}) {
  const busy =
    useIsMutating({ mutationKey: campaignRenameKey(campaign.id) }) > 0;
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Renomear campanha"
    >
      <RenameForm campaign={campaign} onClose={onClose} />
    </Modal>
  );
}

function RenameForm({
  campaign,
  onClose,
}: {
  campaign: GuaranteeCampaignDetail;
  onClose: () => void;
}) {
  const rename = useRenameGuaranteeCampaign(campaign.id);
  const [name, setName] = useState(campaign.name);
  const [error, setError] = useState<string>();
  return (
    <form
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return setError('Dê um nome à campanha.');
        rename.mutate(name.trim(), { onSuccess: onClose });
      }}
    >
      <Input
        id="guarantee-campaign-rename"
        label="Nome"
        autoFocus
        maxLength={120}
        value={name}
        error={error}
        readOnly={rename.isPending}
        onChange={(e) => {
          setName(e.target.value);
          setError(undefined);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={rename.isPending}
          onClick={onClose}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={rename.isPending}>
          Salvar
        </Button>
      </div>
    </form>
  );
}
